/// <amd-module name="VC/Common/Components/CommandBarRepositorySelector" />
import React = require("react");

import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IMenuItem } from "VSSUI/Menu";
import { IVssSecurityService } from "VSS/Platform/Security";
import { css, Components, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { LegacyComponent } from "VSS/Legacy/Legacy";

import { CommandBarExpandableComponent, IComponentWithExpandableProps } from "VSS/Features/Frame/CommandBar";
import { Observer } from "VSSUI/Observer";
import { IPickListAction, IPickList } from "VSSUI/PickList";
import { IRepositoryPickerItem } from "../RepositoryPickerProvider";
import { RepositoryDropdownComponent } from "./RepositoryDropdown";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVCNavigationData, IVCNavigationService } from "../Navigation";
import { GitRepositoryPermissions, GitConstants } from "VC/Common/Contracts/Constants";
import * as CommonResources from "VC/Common/Resources";

interface ICommandBarRepositorySelectorProps extends IComponentWithExpandableProps {
    hideIcon?: boolean;
    useSmallDropdownIcon?: boolean;
}

interface ICommandBarRepositorySelectorState {
    selected: boolean;
}

const REPOSITORY_SELECTOR_CONTRIBUTION_ID: string = "ms.vss-code-web.command-bar-repository-picker-actions";

class CommandBarRepositorySelector extends VssComponent<ICommandBarRepositorySelectorProps, ICommandBarRepositorySelectorState> {

    private vcData?: IVCNavigationData;
    private projectId?: string;
    private projectName?: string;
    private canCreateRepositories?: boolean;
    private canManageRepositories?: boolean;
    private repositoryDropdown = React.createRef<RepositoryDropdownComponent>();
    private repositoryActions: IPickListAction[];
    private repositoryContributedActions: IReadonlyObservableArray<IMenuItem>;

    constructor(props: ICommandBarRepositorySelectorProps, context: IVssComponentContext) {
        super(props, context);

        this.initializeData(props, context);
        this.state = { selected: false };
    }

    public render(): JSX.Element | null {
        return (
            this.vcData && this.projectName ? (
                <CommandBarExpandableComponent
                    {...this.props}
                    calloutId={this.props.expanded ? "dropdown" + this.componentId : undefined}
                    className={css("commandbar-item-menu", this.props.className)}
                    expandOnHover={false}
                    id={"cmec" + this.componentId}
                    noTabIndex={true}
                    expand={this.expand}
                >
                    <Observer contributedActions={this.repositoryContributedActions}>
                        {(props: { contributedActions: IMenuItem[] }) => {
                            return <RepositoryDropdownComponent
                                ref={this.repositoryDropdown}
                                className="command-bar-repository-selector"
                                id={"dropdown" + this.componentId}
                                tabIndex={this.props.selected ? 0 : -1}
                                onFocus={this.props.onFocus}
                                onFocusLost={this.props.onFocusLost}
                                hideSelectedItemIcon={this.props.hideIcon}
                                dropdownIconProps={this.props.useSmallDropdownIcon ? { iconName: "ChevronDownSmall" } : undefined}
                                providerOptions={{
                                    actions: [
                                        ...this.repositoryActions, 
                                        ...this.menuItemsToPickListActions(props.contributedActions) 
                                    ],
                                    getItemHref: this.getItemHref,
                                    projectName: this.projectName,
                                    projectId: this.projectId,
                                    selectedRepositoryId: this.vcData && this.vcData.defaultRepoId || "tfvc",
                                    selectedRepositoryName: this.vcData && this.vcData.defaultGitRepoName,
                                    selectedRepositoryIsTfvc: this.vcData && !this.vcData.defaultRepoIsGit,
                                    selectedRepositoryIsFork: this.vcData && this.vcData.defaultRepoIsFork,
                                    supportsTfvc: this.vcData && this.vcData.supportsTfvc
                                }}
                                isOpen={this.props.expanded}
                                onToggleDropdown={this.onToggleDropdown}
                            />
                        }}
                    </Observer>
                </CommandBarExpandableComponent>
            ) : (
                null
            )
        );
    }

    public componentDidUpdate(prevProps: ICommandBarRepositorySelectorProps) {
        if (!prevProps.selected && this.props.selected && this.repositoryDropdown.current) {
            this.repositoryDropdown.current.focus();
        }
    }

    private expand = (key: string, isOpen: boolean, reason?: string) => {
        if (this.props.expand) {
            this.props.expand(key, isOpen, reason);
        }
    }

    private onToggleDropdown = (isOpen: boolean) => {
        if (this.props.expand && this.props.itemKey) {
            this.props.expand(this.props.itemKey, isOpen);
        }
    }

    private initializeData(props: ICommandBarRepositorySelectorProps, context: IVssComponentContext): void {
        const navigationService = context.pageContext.getService<IVCNavigationService>("IVCNavigationService");
        this.vcData = navigationService.getData();
        
        const tfsPageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            this.projectName = tfsPageData.project.name;
            this.projectId = tfsPageData.project.id;

            const securityService = context.pageContext.getService<IVssSecurityService>("IVssSecurityService");
            const securityToken = `repoV2/${this.projectId}/`;

            this.canCreateRepositories = securityService.hasPermission(GitConstants.GitSecurityNamespaceId, securityToken, GitRepositoryPermissions.CreateRepository);
            this.canManageRepositories = 
                securityService.hasPermission(GitConstants.GitSecurityNamespaceId, securityToken, GitRepositoryPermissions.RenameRepository) ||
                securityService.hasPermission(GitConstants.GitSecurityNamespaceId, securityToken, GitRepositoryPermissions.DeleteRepository) ||
                securityService.hasPermission(GitConstants.GitSecurityNamespaceId, securityToken, GitRepositoryPermissions.ManagePermissions);
        }
        else {
            this.projectName = undefined;
            this.projectId = undefined;
        }

        this.repositoryActions = this.getActions();
        this.repositoryContributedActions = getMenuItems(this.context.pageContext, REPOSITORY_SELECTOR_CONTRIBUTION_ID);
    }

    public hasFocus(focusElement?: HTMLElement): boolean {
        return false;
    }

    private getActions = (): IPickListAction[]  => { 
        const actions: IPickListAction[] = [];

        if (this.canCreateRepositories) {
            // New Repository Action
            actions.push({
                name: CommonResources.NewRepository,
                iconProps: { iconName: "Add" },
                onClick: () => {
                    const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.renderCallout((onDismiss) => {
                        return React.createElement(LegacyComponent, {
                            modules: ["VersionControl/Scripts/Components/HeaderComponents/HeaderComponents"],
                            wrappedType: "newRepositoryDialog",
                            onClose: () => { onDismiss(); }
                        }, null);
                    });
                }
            });

            // Import Repository Action
            actions.push({
                name: CommonResources.ImportRepository,
                iconProps: { iconName: "Upload" },
                onClick: () => {
                    const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.renderCallout((onDismiss) => {
                        return React.createElement(LegacyComponent, {
                            modules: ["VersionControl/Scripts/Components/HeaderComponents/HeaderComponents"],
                            wrappedType: "importRepositoryDialog",
                            onClose: () => { onDismiss(); }
                        }, null);
                    });
                }
            });
        }

        if (this.canManageRepositories) {
            // Manage Repositories Action
            const adminUrl = routeUrl(this.context.pageContext, "ms.vss-code-web.admin-version-control-route", {
                project: this.projectName!
            });
            actions.push({
                name: CommonResources.ManageRepositories,
                iconProps: { iconName: "Settings" },
                href: adminUrl
            });
        }

        return actions;
    }

    private menuItemsToPickListActions = (menuItems: IMenuItem[]): IPickListAction[] => {
        const pickListActions: IPickListAction[] = [];

        menuItems.forEach(item => {
            pickListActions.push({
                name: item.text || "",
                iconProps: item.iconProps,
                onClick: item.onActivate,
                href: item.href,
            });
        });

        return pickListActions;
    }

    private getItemHref = (repositoryItem: IRepositoryPickerItem)  => { 
        const navigationService = this.context.pageContext.getService<IVCNavigationService>("IVCNavigationService");
        return navigationService.getRepositoryUrl(repositoryItem.isTfvc ? null : repositoryItem.name);
    }
}

Components.add("navigation-repository-selector", CommandBarRepositorySelector);
